import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { useNavigate } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import {
  Box,
  Button,
  Divider,
  TextField,
  List,
  ListItem,
  Modal,
  IconButton,
  Typography,
  Skeleton,
  Switch,
  Grid,
  Tooltip
} from '@mui/material';

import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  FileCopy as CloneIcon,
} from '@mui/icons-material';
import SearchBox from '../Common/SearchBox/index.tsx';
import Pagination from '../Common/Pagination/index.tsx';
import {
  ConstantActions,
  FlowBuilderActions,
  LeadFormActions,
  TemplateActions,
} from '../../../Services/_actions/index.js';
import { useSnackBar } from '../Common/SnackBar/index.tsx';
import ConfirmBox from '../Common/ConfirmBox/index.tsx';
import {
  CampaignList,
  Flow,
  Form,
  FormData,
  ValidationErrors,
} from '../../../interfaces/Flowbuilder.interface.ts';
import { TemplateFilters, TemplateResponse } from '../../../interfaces/Template.interface.ts';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import BotNumberDropdown from '../../Shared/BotNumberDropdown/index.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { flowsTableStateAtom } from '../../../Services/_states/index.js';
import { generateFilterKey } from '../../../utils/helper.jsx';
import CreateEditModal from './CreateEditModal.tsx';
import { fetchFlowListSelector } from '../../../Services/_states/FlowBuilder/flowSelector.ts';
import useDebounce from '../../Shared/Delay/index.tsx';
import BotFlowHelp from './BotFlowHelp.tsx';
import LoadingDisableWrapper from '../../Shared/LoadingDisableWrapper/index.tsx';
import { globalpermissions } from '../../../Services/Permission.js';
import { authzContextAtom } from '../../../authorization/authzState';
import { canAccess } from '../../../authorization/canAccess';
import { ACCESS } from '../../../constants/authz';

const FlowList = () => {
  const loggedInUser = useRecoilValue(userInformationAtom)
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const actions = FlowBuilderActions();
  const [channelList, setChannelList] = useState([]);
  const [flowFilters, setFlowFilters] = useState({
    page: 0,
    rowsPerPage: 10,
    search: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
    totalCount: 0,
    organisationId: "",
    selectedOrganisation: null,
    botNumber: "",
    channelObj: null
  });
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    patterns: [],
    tags: [],
    is_published: true,
    is_deleted: false,
    is_default: false,
    waba_id: '',
    phone_number_id: '',
    company_id: '',
    organisation_name: '',
    leads_form_name: '',
    app_name: '',
    campaign_id: '',
    campaign_name: '',
    lead_form_id: '',
    template_name: '',
    template_id: '',
    selectedCampaign: null,
    selectedForm: null,
    selectedOrganisation: null,
    selectedPhoneNumber: null,
    selectedTemplate: null,
  });
  const [flowId, setFlowId] = useState(null);
  const [modalTitle, setModalTitle] = useState('Add A New Flow');
  const [openModal, setOpenModal] = useState(false);
  const [flowListData, setFlowListData] = useState<Flow[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({
    organisation: '',
    phoneNumber: '',
    patterns: '',
    title: '',
  });
  const [openConfirmBox, setOpenConfirmBox] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<Flow | null>(null);
  const [tableState, setTableState] = useRecoilState(flowsTableStateAtom);
  const fetchedData: any = useRecoilValueLoadable(fetchFlowListSelector);
  const [searchQuery, setSearchQuery] = useState(tableState?.filters?.search || '');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [clonedFlowData, setClonedFlowData] = useState(null);
  const constantActions = ConstantActions();
  const authz = useRecoilValue(authzContextAtom);
  const canCreateFlow = authz.isAuthzV2Enabled
    ? canAccess(authz, ACCESS.FLOW_CREATE)
    : constantActions.userHasPermission([globalpermissions?.flowBuilder?.createFlow]);
  const canEditFlow = authz.isAuthzV2Enabled
    ? canAccess(authz, ACCESS.FLOW_UPDATE)
    : constantActions.userHasPermission([globalpermissions?.flowBuilder?.editFlow]);
  const canDeleteFlow = !authz.isAuthzV2Enabled || canAccess(authz, ACCESS.FLOW_DELETE);
  const canPublishFlow = !authz.isAuthzV2Enabled || canAccess(authz, ACCESS.FLOW_PUBLISH);
  const canOpenBuilder = !authz.isAuthzV2Enabled || canAccess(authz, ACCESS.FLOW_BUILDER);

  useEffect(() => {
    if (debouncedSearch !== tableState.filters.search) {
      setTableState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          search: debouncedSearch,
          page: 0
        },
        cache: {},
      }));
    }
  }, [debouncedSearch, setTableState]);

  useEffect(() => {
    if (fetchedData.state === "hasValue" && !fetchedData?.contents?.fromCache) {
      const filterKey = generateFilterKey(tableState.filters);
      const data = fetchedData?.contents?.data || [];
      const totalCount = fetchedData?.contents?.totalCount || 0;

      setTableState((prev) => ({
        ...prev,
        cache: { ...prev.cache, [filterKey]: data },
        totalCountCache: { ...prev.totalCountCache, [filterKey]: totalCount },
      }));
    }
  }, [fetchedData, setTableState]);

  // const handleSearch = (query) => {
  //   setSearchQuery(query);
  // };
  const handleSearch = (query) => {
    const value = query
      .replace(/^\s+/, '')
      .replace(/\s{2,}/g, ' ');

    setSearchQuery(value);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setFlowId(null);
    setClonedFlowData(null);
  };

  const clearFilters = () => {
    setFlowFilters({
      page: 0,
      rowsPerPage: 10,
      search: '',
      sortBy: 'created_at',
      sortOrder: 'desc',
      totalCount: 0,
      organisationId: "",
      selectedOrganisation: null,
      botNumber: "",
      channelObj: null
    });

    setChannelList([]);
    setTableState((prev) => ({
      ...prev,
      filters: {
        ...prev.filters,
        page: 0,
        search: '',
        organisationId: '',
        selectedOrganisation: null,
        botNumber: '',
        channelObj: null,
      },
      cache: {},
    }));
  };

  const handleUpdateFlow = (flow) => {
    setModalTitle('Update Flow');
    setFlowId(flow?._id);
    setFormData(flow);
    setOpenModal(true);
  };

  const handleCloneFlow = (flow) => {
    setModalTitle('Copy Flow');
    setFlowId(null);
    setClonedFlowData(flow);
    setOpenModal(true);
  };

  const handleToggleFlowStatus = async (flow) => {
    try {
      const updatedFlow = { ...flow, is_published: !flow.is_published };
      await actions.updateFlow(flow._id, updatedFlow);
      setTableState((prevState) => {
        const updatedCache: any = Object.entries(prevState.cache).reduce((acc, [key, flowList]) => {
          const updatedList = flowList?.map((item) =>
            item._id === flow._id ? {
              ...item, is_published: updatedFlow.is_published
            } : item
          );
          acc[key] = updatedList;
          return acc;
        }, {});

        return { ...prevState, cache: updatedCache };
      });


      showSnackBar(
        `Flow ${updatedFlow.is_published ? 'Activated' : 'Deactivated'} Successfully`,
        'success'
      );
    } catch (error) {
      showSnackBar('Error updating flow status. Please try again.', 'error');
    }
  };
  const handleDeleteFlow = async (flow) => {
    setFlowToDelete(flow);
    setOpenConfirmBox(true);

  };
  const handleConfirmDelete = async (confirmed) => {
    if (confirmed && flowToDelete) {
      try {
        await actions.deleteFlow(flowToDelete._id);
        showSnackBar('Flow Deleted Successfully', 'success');
        setTableState((prevState) => {
          const updatedCache: any = Object.entries(prevState.cache).reduce(
            (acc, [key, list]) => {
              const updatedList = Array.isArray(list)
                ? list.filter((flow) => flow?._id !== flowToDelete._id)
                : [];
              acc[key] = updatedList;
              return acc;
            },
            {}
          );
          return { ...prevState, cache: updatedCache };
        });
      } catch (error) {
        showSnackBar('Error deleting flow. Please try again.', 'error');
      }
    }
    setOpenConfirmBox(false);
  };

  const handleSettingsClick = (flow) => {
    navigate(`/r/flow/builder/${flow?._id}`);
  };

  const handleChangePage = (event, newPage) => {
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, page: newPage },
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10) || 10;
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, rowsPerPage: value, page: 0, },
    }));
  };

  const [viewMoreOpen, setViewMoreOpen] = useState(false);
  const [currentFlowKeywords, setCurrentFlowKeywords] = useState<any>([]);
  const [flowIdToUpdate, setFlowIdToUpdate] = useState(null);
  const [newKeywords, setNewKeywords] = useState('');

  const sampleFileContent = "keyword1\nkeyword2\nkeyword3";

  const handleDownloadSampleFile = () => {
    const blob = new Blob([sampleFileContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_keywords.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleViewMoreClick = (keywords, flowId) => {
    setCurrentFlowKeywords(keywords);
    setFlowIdToUpdate(flowId);
    setViewMoreOpen(true);
  };

  const handleAddKeywords = () => {
    const newKwList = newKeywords
      .split(',')
      .map((kw) => kw.trim())
      .filter((kw) => kw && !currentFlowKeywords.includes(kw));
    setCurrentFlowKeywords([...currentFlowKeywords, ...newKwList]);
    setNewKeywords('');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      const fileContent: any = e?.target?.result;
      const uploadedKeywords: any = fileContent
        .split('\n')
        .map((kw) => kw.trim())
        .filter((kw) => kw);
      setCurrentFlowKeywords([...currentFlowKeywords, ...uploadedKeywords]);
    };

    if (file) {
      reader.readAsText(file);
    }
  };

  const saveKeywords = async () => {
    try {
      const flowToUpdate = flowListData.find((flow) => flow._id === flowIdToUpdate);

      if (!flowToUpdate) {
        showSnackBar('Flow not found. Unable to update keywords.', 'error');
        return;
      }
      const updatedFlow = { ...flowToUpdate, patterns: currentFlowKeywords };
      const response: any = await actions.updateFlow(flowIdToUpdate, updatedFlow);

      if (response?.status === 200) {
        setFlowListData((prevList) =>
          prevList.map((flow) =>
            flow._id === flowIdToUpdate ? { ...flow, patterns: currentFlowKeywords } : flow
          )
        );
        showSnackBar('Keywords updated successfully.', 'success');
      } else {
        showSnackBar('Failed to update keywords. Please try again.', 'error');
      }
    } catch (error) {
      showSnackBar('An error occurred while updating keywords.', 'error');
    }
    setViewMoreOpen(false);
  };

  const breadcrumbsData = [
    { label: <IconButton color='primary'><HomeIcon /></IconButton>, onClick: () => navigate("/") },
    { label: 'Flows' },
  ];

  const downloadBotFlows = () => {
    try {
      const res = actions.downloadBotFlows(flowFilters)
    } catch (error) {
      showSnackBar(error?.toString(), 'error');
    }
  }

  return (
    <Box>
      <DynamicBreadcrumbs items={breadcrumbsData}
        onBreadcrumbClick={(event, item) => {
          if (item?.onClick) {
            item.onClick();
          }
        }} />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={4} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>Flows</Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          {canCreateFlow && (
            <Tooltip title="Add a New Flow">
              <Button
                size="small"
                onClick={() => {
                  setModalTitle('Add A New Flow');
                  setFlowId(null);
                  setClonedFlowData(null);
                  setValidationErrors({ organisation: '', phoneNumber: '', patterns: '', title: '' });
                  setChannelList([]);
                  setFormData({
                    title: '',
                    patterns: [],
                    tags: [],
                    is_published: true,
                    is_deleted: false,
                    is_default: false,
                    waba_id: '',
                    phone_number_id: '',
                    company_id: '',
                    organisation_name: '',
                    campaign_id: '',
                    campaign_name: '',
                    lead_form_id: '',
                    leads_form_name: '',
                    app_name: '',
                    template_id: '',
                    template_name: '',
                    selectedCampaign: null,
                    selectedForm: null,
                    selectedOrganisation: null,
                    selectedPhoneNumber: null,
                    selectedTemplate: null,
                  });
                  setOpenModal(true);
                }}
                variant="contained"
              >
                Add
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Download Flow">
            <Button
              size="small"
              onClick={downloadBotFlows}
              variant="contained"
              sx={{ ml: 2 }}
            >
              Download
            </Button>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ alignItems: 'center', paddingTop: '8px', paddingBottom: '16px' }} >
        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' }, gap: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
            <LoadingDisableWrapper loading={fetchedData.state === "loading"}>
              <SearchBox
                handleSearch={handleSearch}
                value={searchQuery}
                disabled={fetchedData.state === "loading"}
              />
            </LoadingDisableWrapper>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2 }, ml: 'auto', flexWrap: { xs: 'wrap', sm: 'nowrap' }, width: { xs: '100%', sm: 'auto' } }}>
            {loggedInUser?.is_superuser && (
              <OrganisationDropdown
                sx={{ minWidth: 165 }}
                label={"Select Organisation"}
                value={tableState?.filters?.selectedOrganisation || null}
                onChange={(value) => {
                  setTableState((prev) => ({
                    ...prev,
                    filters: { ...prev.filters, selectedOrganisation: value, organisationId: value?.organisation_id || '', channelObj: null, botNumber: "", },
                  }));
                }
                }
              />
            )}

            <BotNumberDropdown
              sx={{ minWidth: 160 }}
              organisation={loggedInUser?.is_superuser ? tableState?.filters?.selectedOrganisation || null : loggedInUser?.profile?.organisation || null}
              // organisation={tableState?.filters?.selectedOrganisation || null}
              onChange={(selected) => {
                setTableState((prev) => ({
                  ...prev,
                  filters: { ...prev.filters, botNumber: selected?.wa_phone_number || '', channelObj: selected, },
                }));
              }}
              value={tableState?.filters?.channelObj || null}
              label="Select Bot Number"
            />
          </Box>
          <Tooltip title="Clear Filters">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => clearFilters()}
            >
              Clear
            </Button></Tooltip>
          <Tooltip title="Help">
            <IconButton >
              <BotFlowHelp />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <Divider />
      <List sx={{
        padding: 1, maxHeight: 'calc(100vh - 300px)',
        width: '100%',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        }
      }}>
        {fetchedData.state === "loading" ? (
          Array.from(new Array(5)).map((_, index) => (
            <ListItem
              key={index}
              divider
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Skeleton variant="text" width="30%" />
              <Skeleton variant="text" width="30%" />
              <Skeleton variant="circular" width={40} height={40} />
            </ListItem>
          ))
        ) : tableState?.cache[generateFilterKey(tableState?.filters)]?.length > 0 ? (
          tableState?.cache[generateFilterKey(tableState?.filters)]?.map((flow, index) => (
            <ListItem
              key={index}
              divider
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1">
                  <strong>{tableState?.filters?.page * tableState?.filters?.rowsPerPage + index + 1}. {flow?.title || 'Untitled Flow'}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Organization:</strong> {flow?.organisation_name || 'N/A'}, <strong>Business Service:</strong>{' '}
                  {flow?.campaign_name ? ` (${flow.campaign_name})` : ''}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  <strong>Keywords:</strong>{' '}
                  {flow?.patterns?.slice(0, 3).join(', ') || 'None'}
                  {flow?.patterns?.length > 3 && (
                    <Button
                      size="small"
                      onClick={() => handleViewMoreClick(flow.patterns, flow._id)}
                      sx={{ textTransform: 'none', marginLeft: 1 }}
                    >
                      View More
                    </Button>
                  )}
                </Typography>
              </Box>

              <Box sx={{ flex: 1, textAlign: 'right' }}>

                <Tooltip
                  title={flow.is_published ? 'Deactivate Flow' : 'Activate Flow'}
                  arrow
                >
                  <span>
                    <Switch
                      checked={!!flow.is_published}
                      disabled={!canPublishFlow}
                      onChange={() => handleToggleFlowStatus(flow)}
                    />
                  </span>
                </Tooltip>

                {canEditFlow && (
                  <Tooltip title="Edit Flow" arrow>
                    <IconButton onClick={() => handleUpdateFlow(flow)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {canCreateFlow && (
                  <Tooltip title="Clone Flow" arrow>
                    <IconButton onClick={() => handleCloneFlow(flow)}>
                      <CloneIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {canDeleteFlow && (
                  <Tooltip title="Delete Flow" arrow>
                    <IconButton onClick={() => handleDeleteFlow(flow)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {canOpenBuilder && (
                  <Tooltip title="Open in Flow Builder" arrow>
                    <IconButton onClick={() => handleSettingsClick(flow)}>
                      <SettingsIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

            </ListItem>
          ))
        ) : (
          <Typography align="center">No flows available.</Typography>
        )}
      </List>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, marginTop: 0 }}>
        <Pagination
          totalCount={tableState?.totalCountCache[generateFilterKey(tableState?.filters)] || 0}
          rowsPerPage={tableState?.filters?.rowsPerPage}
          page={tableState?.filters?.page}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Box>
      <ConfirmBox
        open={openConfirmBox}
        onClose={handleConfirmDelete}
        options={{
          title: 'Delete Flow',
          text: 'Are you sure you want to delete this flow?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'Cancel',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />
      <Modal open={viewMoreOpen} onClose={() => setViewMoreOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: '8px',
            p: 3,
            width: 400,
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Manage Keywords
          </Typography>
          <TextField
            label="Keywords"
            fullWidth
            value={currentFlowKeywords.join(',')}
            margin="dense"
            variant="outlined"
            size="small"
            InputProps={{
              readOnly: true,
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Add Keywords (comma-separated)"
            fullWidth
            value={newKeywords}
            onChange={(e) => setNewKeywords(e.target.value)}
            margin="dense"
            variant="outlined"
            size="small"
          />

          <Box mt={1} display="flex" alignItems="center" gap={2}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleAddKeywords}
              sx={{ textTransform: 'none' }}
            >
              Add Keywords
            </Button>
            <Button
              variant="contained"
              component="label"
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Upload File
              <input
                type="file"
                accept=".txt"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadSampleFile}
              sx={{ textTransform: 'none' }}
            >
              Download Sample File
            </Button>
          </Box>

          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => setViewMoreOpen(false)}
              size="small"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveKeywords}
              size="small"
              color="primary"
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
      <CreateEditModal open={openModal} onClose={handleModalClose} modalTitle={modalTitle} flowId={flowId} cloneFrom={clonedFlowData} />
    </Box>
  );
};

export default FlowList;
